import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment'
import { HttpClient, HttpParams, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { AuthService, FileService, CacheService, IUserAmapBO, IUserEvaluableUserBO, IUserRoleBO, IUserProfileBO, IOrganizationalStructureBO, IUserCategoryBO } from '@mapfre-tech/amap3';
import { map, mergeMap } from 'rxjs/operators';
import { Observable, throwError, forkJoin, of, Subject } from 'rxjs';
import moment from 'moment';
import * as _ from 'lodash';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  className = 'UserService';

  constructor(private http: HttpClient, private authService: AuthService, private fileService: FileService,
    private cacheService: CacheService, private router: Router) { }

  /**
   * The API returns users without fullName/txtFullname, which are the fields used by the user selectors
   */
  private withFullName<T extends object>(users: T[]): T[] {
    return (users ?? []).map((user: T & { txtName?: string; txtLastname1?: string; txtFullname?: string; fullName?: string }) => {
      const fullName = [user.txtName, user.txtLastname1].filter(Boolean).join(' ');
      user.fullName = fullName;
      user.txtFullname = fullName || user.txtFullname || '';
      return user;
    });
  }

  getAllUsers(cache = true, cacheKey = "allUsers"): Observable<IUserAmapBO[]> {
    const isAdminSection = _.includes(this.router.url, 'administration');
    cacheKey = cacheKey+"_"+isAdminSection.toString()
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey) as object as Observable<IUserAmapBO[]>;
   } else {
      const observable = this.http.get<IUserAmapBO[]>(environment.base_URL + `/users`).pipe(map((users) => {
         const res = this.withFullName(users);
         if(isAdminSection){
            this.cacheService.updateCacheData(res, cacheKey)
            return res;
         }else{
            const filtrado =  _.filter(res, it => it.mrkActive);
            this.cacheService.updateCacheData(filtrado, cacheKey)
            return filtrado;
         }
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable as object as Observable<IUserAmapBO[]>;
   }
  }

  getUserByRoleForkJoin(role: string[][], os = null, cache = true) {
    const result = forkJoin(_.map(role, rol => this.getUserByRole(rol, os, cache)));
    return result.pipe(map((res) => { return _.uniqBy(_.flatten(res), 'idUserPk') }));
  }

  getUserByRole(role: string[], os: IOrganizationalStructureBO[] | null = null, cache = true): Observable<IUserAmapBO[]> {

    let cacheKey = "userRoles";
    const params: any = {};

    if (role) {

      if (!_.isArray(role)) {
        role = [role];
      }

      params.txtTypeRole = role.join(',');
    }
    else {
      role = [];
    }

    if (os) {
      if (!_.isArray(os)) {
        os = [os];
      }
      params.orgStrIds = os.join(',');
    }
    else {
      os = []
    }

    if(params.orgStrIds){
      cacheKey = cacheKey + "_" + params.orgStrIds;
    }else {
      cacheKey = cacheKey + "_" + "null";
    }
    if(params.txtTypeRole){
      cacheKey = cacheKey + "_" + params.txtTypeRole;
    }else {
      cacheKey = cacheKey + "_" + "null";
    }

    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey) as object as Observable<IUserAmapBO[]>;
    } else {
      const observable = this.http.get<IUserAmapBO[]>(environment.base_URL + `/users/roles`, { params }).pipe(map((users) => {
        const res = this.withFullName(users);
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getUserProfile(cache = true, cacheKey = "userProfile"): Observable<IUserProfileBO> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey) as object as Observable<IUserProfileBO>;
    } else {
      const observable = this.http.get<IUserProfileBO>(environment.base_URL + `/users/profile`).pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }

  }

  getUserProfileById(userId: number): Observable<IUserProfileBO> {
    const params: any = {};
    params.onlySign = true;
    if (userId) {
      params.userId = userId;
    }
    return this.http.get<IUserProfileBO>(environment.base_URL + `/users/profile`, { params });
  }

  getUserProfileNoCache(userId = null): Observable<IUserProfileBO> {
    const params: any = {};
    if (userId) {
      params.userId = userId;
    }
    return this.http.get<IUserProfileBO>(environment.base_URL + `/users/profile`, { params });
  }


  getUserEvaluationProfile(userId: number[] | null = null, questionnaireTypeIds: number[] | null, exercises: any = null, evaluator: any = null): Observable<IUserEvaluableUserBO[]> {
    const params: any = {};

    if (questionnaireTypeIds) {
      params.questionnaireTypeIds = questionnaireTypeIds.join(',');
    }
    if (exercises) {
      params.exercises = exercises.join(',');
    }
    if (evaluator != null) {
      params.evaluator = evaluator;
    }
    
    params.userId = userId; // We always want to send this parameter. The lack of it will produce a fail.
    
    return this.http.get<IUserEvaluableUserBO[]>(environment.base_URL + "/users/evaluations", { params }).pipe(map(res => {
      return _.map(res, (q: any) => {
        let totalValoration = 0;
        let totalNumerableQuestions = 0;
        _.forEach(q.answers, (e) => {
          const question = _.get(e, 'question');
          const isNumerable = _.get(question, 'optionQuestion');
          const valoration = _.get(e, 'mumEvaluationValue', 0);
          if (isNumerable) {
            totalNumerableQuestions++;
            totalValoration += valoration;
          }
        })
        q.answers = _.sortBy(q.answers, (e) => {
          return _.get(e, 'nmrOrder');
        });
        q.questionnaire.valoration = totalNumerableQuestions ? _.round((totalValoration / totalNumerableQuestions), 2) : 0;
        return q;
      })
    }));
  }

  getUserBycdNuuma(nuuma: string, cache = true, cacheKey = "userByNuuma"): Observable<IUserAmapBO>{
    cacheKey = cacheKey+"_"+nuuma.toString()
    
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey) as object as Observable<IUserAmapBO>;
    } else {
      const observable = this.http.get<IUserAmapBO>(environment.base_URL + `/users/${nuuma}`).pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  newUser(newUser: IUserAmapBO) {
    return this.http.post(environment.base_URL + "/users", newUser, { observe: 'response' });
  }

  newUserProfile(newUser: IUserProfileBO): Observable<IUserProfileBO> {
    return this.http.post<IUserProfileBO>(environment.base_URL + "/users/profile", newUser);
  }

  updateUser(editedUser: IUserAmapBO, idUserPk: number): Observable<IUserAmapBO> {
    return this.http.put<IUserAmapBO>(environment.base_URL + `/users/${idUserPk}`, editedUser)
  }

  newUserWithFiles(newUser: IUserAmapBO) {
    const filesToUpload: any = {}
    if (_.get(newUser, 'filePhotoName.type')) {
      const file = _.get(newUser, 'filePhotoName');
      filesToUpload['user-photo'] = file;
      const filename = _.get(file, 'name');
      newUser = _.set(newUser, 'filePhotoName', filename);
    }
    if (_.get(newUser, 'fileFirmName.type')) {
      const file = _.get(newUser, 'fileFirmName');
      filesToUpload['user-sign'] = file;
      const filename = _.get(file, 'name');
      newUser = _.set(newUser, 'fileFirmName', filename);
    }
    if (_.get(newUser, 'fileCvEsName.type')) {
      const file = _.get(newUser, 'fileCvEsName');
      filesToUpload['user-cv-es'] = file;
      const filename = _.get(file, 'name');
      newUser = _.set(newUser, 'fileCvEsName', filename);
    }
    if (_.get(newUser, 'fileCvEnName.type')) {
      const file = _.get(newUser, 'fileCvEnName');
      filesToUpload['user-cv-en'] = file;
      const filename = _.get(file, 'name');
      newUser = _.set(newUser, 'fileCvEnName', filename);
    }

    if (!_.isEmpty(_.values(filesToUpload))) {
      return this.newUser(newUser).pipe(mergeMap((res: any) => {
        return this.fileService.upload(filesToUpload['user-photo'], 'user-photo', res).pipe(mergeMap((res1: any) => {
          return this.fileService.upload(filesToUpload['user-sign'], 'user-sign', res).pipe(mergeMap((res2: any) => {
            return this.fileService.upload(filesToUpload['user-cv-es'], 'user-cv-es', res).pipe(mergeMap((res3: any) => {
              return this.fileService.upload(filesToUpload['user-cv-en'], 'user-cv-en', res).pipe(map((res4: any) => {
                return res;
              }));
            }));
          }));
        }))
        /*let observables = _.map(filesToUpload, (value, key) => this.fileService.upload(value, key, res));
return forkJoin(observables).pipe(map(resultFiles => {
  return res;
}));*/
      }))
    }
    else {
      return this.newUser(newUser);
    }
  }

  updateUserWithFiles(editedUser: IUserAmapBO, idUserPk: number) {
    const filesToUpload: any = {}
    if (_.get(editedUser, 'filePhotoName.type')) {
      const file = _.get(editedUser, 'filePhotoName');
      filesToUpload['user-photo'] = file;
      const filename = _.get(file, 'name');
      editedUser = _.set(editedUser, 'filePhotoName', filename);
    }
    if (_.get(editedUser, 'fileFirmName.type')) {
      const file = _.get(editedUser, 'fileFirmName');
      filesToUpload['user-sign'] = file;
      const filename = _.get(file, 'name');
      editedUser = _.set(editedUser, 'fileFirmName', filename);
    }
    if (_.get(editedUser, 'fileCvEsName.type')) {
      const file = _.get(editedUser, 'fileCvEsName');
      filesToUpload['user-cv-es'] = file;
      const filename = _.get(file, 'name');
      editedUser = _.set(editedUser, 'fileCvEsName', filename);
    }
    if (_.get(editedUser, 'fileCvEnName.type')) {
      const file = _.get(editedUser, 'fileCvEnName');
      filesToUpload['user-cv-en'] = file;
      const filename = _.get(file, 'name');
      editedUser = _.set(editedUser, 'fileCvEnName', filename);
    }

    if (!_.isEmpty(_.values(filesToUpload))) {
      return this.updateUser(editedUser, idUserPk).pipe(mergeMap((res: any) => {
        return this.fileService.upload(filesToUpload['user-photo'], 'user-photo', res).pipe(mergeMap((res1: any) => {
          return this.fileService.upload(filesToUpload['user-sign'], 'user-sign', res).pipe(mergeMap((res2: any) => {
            return this.fileService.upload(filesToUpload['user-cv-es'], 'user-cv-es', res).pipe(mergeMap((res3: any) => {
              return this.fileService.upload(filesToUpload['user-cv-en'], 'user-cv-en', res).pipe(map((res4: any) => {
                return res;
              }));
            }));
          }));
        }))
        /*let observables = _.map(filesToUpload, (value, key) => this.fileService.upload(value, key, idUserPk));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));*/
      }))
    }
    else {
      return this.updateUser(editedUser, idUserPk);
    }
  }


  deleteUser(id: number) {
    return this.http.delete(environment.base_URL + `/user/${id}`)
  }

  /* Cambiar a ID */
  getUserByID(userId: number, userNuuma?: string): Observable<IUserAmapBO[]> {

    const params: any = {};

    if (userId || userId === 0) {
      params.userId = userId;
    }

    if (userNuuma) {
      params.userNuuma = userNuuma;
    }

    return this.http.get<IUserAmapBO[]>(environment.base_URL + `/users`, { params }).pipe(map((res) => {
      return this.withFullName(res);
    }));
  }

  newGetUserByRoles(role: IUserRoleBO): Observable<IUserAmapBO[]> {
    return this.getAllUsers().pipe(map<IUserAmapBO[], IUserAmapBO[]>(res => _.filter(res, ['typeRole.txtTypeRole', role])));
  }

  getUsersByOS(id: number[], cache?: any, cacheKey = "getUsersByOS"): Observable<IUserAmapBO> {
    if (this.cacheService.canUseCache(cacheKey, [id]) && cache) {
      return this.cacheService.getCacheData(cacheKey, [id]) as object as Observable<IUserAmapBO>;
    } else {
      const observable = this.http.get<IUserAmapBO>(environment.base_URL + "/users/os/" + id.join(',')).pipe(map((users) => {
        const res = this.withFullName(users as IUserAmapBO[]) as IUserAmapBO;
        this.cacheService.updateCacheData(res, cacheKey, [id])
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey, [id]);
      return observable;
    }

  }

  handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(
      'Something bad happened; please try again later.');
  };


  uploadFile(idUserPk: number, file: FormData) {
    const token = `Bearer ${this.authService.getToken()}`;

    return this.http.post(`http://ec2-34-247-190-210.eu-west-1.compute.amazonaws.com:8080/amap3_be_web/api` + `/users/${idUserPk}/file`,
      file
      , {
        headers: new HttpHeaders().set('authorization', token),
        reportProgress: true
      });
  }

  downloadFile(idUserPk: number, userField: string) {

    const token = `Bearer ${this.authService.getToken()}`;

    return this.http.get(`http://ec2-34-247-190-210.eu-west-1.compute.amazonaws.com:8080/amap3_be_web/api` + `/users/${idUserPk}/file`,
      {
        params: { userField: userField },
        headers: new HttpHeaders().set('authorization', token).set("Accept", "application/octet-stream"),

        responseType: 'blob' as 'json',
        observe: 'response'
      }
    );
  }

  uploadUserFiles(idUserPk: number, files: FormData[]) {
    const result = forkJoin(
      _.map(files, (item) => {
        return this.uploadFile(idUserPk, item);
      })
    );
    return result;
  }

  deleteUserFile(idUserPk: number, userField: string) {

    const token = `Bearer ${this.authService.getToken()}`;

    return this.http.delete(`http://ec2-34-247-190-210.eu-west-1.compute.amazonaws.com:8080/amap3_be_web/api` + `/users/${idUserPk}/file`,
      {
        params: { userField: userField },
        headers: new HttpHeaders().set('authorization', token),
        observe: 'response'
      }
    );
  }

  getUsersByCategoryAndOS(categories: IUserCategoryBO[], os: IOrganizationalStructureBO[]) {
    const params = {
      categoryIds: categories.join(','),
      orgStrIds: os.join(',')
    }
    return this.http.get<IUserAmapBO[]>(environment.base_URL + "/users", { params }).pipe(map(users => this.withFullName(users)));
  }

  deleteCache() {

    const memoryCache = ["profile", "getUsersByOS", "userRoles", "allUsers"];

    _.forEach(memoryCache, item => {
      this.cacheService.deleteCache(item);
    })

  }


}
